import { useEffect, useState, useCallback } from "react";
import { inboxApi } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, Copy, RefreshCw, MailWarning, Radio, ChevronDown } from "lucide-react";

const SEVERITY_COLOR = { critical: "#EF4444", high: "#F59E0B", info: "#71717A" };

export default function InboxMonitor() {
  const [config, setConfig] = useState(null);
  const [signals, setSignals] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([inboxApi.config(), inboxApi.signals()]);
      setConfig(c);
      setSignals(s);
    } catch (e) { console.error("inbox monitor load failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(config?.webhook_url || "");
      toast.success("Webhook URL copied");
    } catch (e) { console.error("clipboard failed", e); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotating invalidates the current webhook URL. Continue?")) return;
    try { await inboxApi.rotateSecret(); toast.success("Webhook secret rotated"); load(); }
    catch (e) { console.error("rotate failed", e); toast.error("Rotation failed"); }
  };

  const routes = config?.routes || [];

  return (
    <section className="uc-card p-6 uc-fade" data-testid="inbox-monitor">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="uc-chip mb-2"><Radio size={11}/> INBOX WIRE · SENTINEL</div>
          <h2 className="font-serif-display text-2xl">Venture Inbox Monitor</h2>
          <p className="text-white/50 text-xs mt-1 max-w-xl">
            Point Mailgun / SendGrid / Postmark or a forwarding script at your unique
            webhook URL below. Every inbound email whose recipient matches a venture email
            spawns a Sentinel insight — red on refund / dispute / fraud keywords.
          </p>
        </div>
        <button
          data-testid="inbox-rotate-btn"
          onClick={rotate}
          className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 hover:text-red-400 px-2 py-1 border border-white/10 hover:border-red-500/40 transition-colors flex items-center gap-1"
        >
          <RefreshCw size={11}/> Rotate
        </button>
      </div>

      {loading && <div className="text-white/40 text-sm">Loading wire…</div>}

      {!loading && (
        <>
          <div className="p-3 bg-black/40 border border-white/10 mb-4">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-1">Webhook URL</div>
            <div className="flex items-center gap-2">
              <code data-testid="inbox-webhook-url" className="flex-1 font-mono-uc text-xs text-teal-300 truncate">{config?.webhook_url}</code>
              <button data-testid="inbox-copy-btn" onClick={copyUrl} className="text-white/60 hover:text-teal-400 transition-colors" title="Copy">
                <Copy size={13}/>
              </button>
            </div>
            <div className="font-mono-uc text-[10px] text-white/40 mt-2">
              POST JSON body: {"{ to, from, subject, body }"}. Path secret gates access — rotate to invalidate any provider you no longer trust.
            </div>
          </div>

          {routes.length === 0 ? (
            <div className="text-[11px] text-amber-300/80 italic py-3 border border-dashed border-amber-500/30 text-center">
              No venture has a dedicated email yet. Open a venture &rarr; set the Venture Email field to route inbound mail.
            </div>
          ) : (
            <div className="mb-4">
              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-2">Routing Table ({routes.length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {routes.map((r) => (
                  <div key={r.venture_id} className="flex items-center justify-between text-[11px] font-mono-uc p-2 border border-white/5" data-testid={`inbox-route-${r.venture_id}`}>
                    <span className="text-white/70 truncate">{r.venture_name}</span>
                    <span className="text-teal-300 truncate ml-2">{r.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            data-testid="inbox-signals-toggle"
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-between font-mono-uc text-[10px] uppercase tracking-widest text-white/50 hover:text-white/80 pt-3 border-t border-white/10"
          >
            <span>Recent Signals · {signals.length}</span>
            <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`}/>
          </button>

          {expanded && (
            <div className="mt-3 space-y-1 max-h-64 overflow-y-auto" data-testid="inbox-signals-list">
              {signals.length === 0 && (
                <div className="text-[11px] text-white/40 italic py-2">No inbox signals yet. Send a test POST to the URL above.</div>
              )}
              {signals.map((s) => (
                <div key={s.id} className="p-2 border border-white/10 text-[11px] font-mono-uc flex items-start gap-2" data-testid={`inbox-signal-${s.id}`}>
                  <span className="uc-dot mt-1 flex-none" style={{ background: SEVERITY_COLOR[s.severity] || "#555" }}></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <span className="text-white/80 truncate">{s.subject || "(no subject)"}</span>
                      <span className="text-white/40 flex-none">{new Date(s.created_at).toLocaleString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                    </div>
                    <div className="text-white/50 truncate">{s.from} &rarr; {s.to}</div>
                    {s.matched_terms?.length > 0 && (
                      <div className="text-red-300 text-[10px] mt-1 flex items-center gap-1"><MailWarning size={10}/> {s.matched_terms.join(", ")}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
